import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom/cjs/react-router-dom';

import {UserType, projectStatusEnum, routeToRedirectEditProject, routes} from '../../../util/uiconstants';
import {labels} from '../../../util/utils';
import selectAccessPermissions from '../../../reducers/Project/selectors/selectAccessPermissions';

import {clearErrors} from '../../../actions/erroractions';
import {setIsModalOpen} from '../../../actions/AccessGroups/SetIsModalOpen';
import {getProjectToEdit, setEditMode} from '../../../actions/EditProject/editprojectactions';

import CardBackground from '../../CoreWrappers/CardBackground';
import Name from './Name';
import DraftIndicator from './DraftIndicator';
import StyledNameGrid from './StyledNameGrid';
import StyledContentGrid from './StyledContentGrid';
import StyledModules from './Module/StyledModules';
import DashboardModule from './Module/DashboardModule';
import GRAModule from './Module/GRAModule';
import ContactListModule from './Module/ContactListModule';
import ProjectWizardModule from './Module/ProjectWizardModule';
import StyledUserLists from './UserList/StyledUserLists';
import ProjectOwnerList from './UserList/ProjectOwnerList';
import ClientList from './UserList/ClientList';
import Menu from './Menu';
import MarkAsCompleteModal from './MarkAsCompleteModal';
import RollForwardModal from './RollForwardModal';
import RemoveModal from './RemoveModal';
import DeleteModal from './DeleteModal';
import RollForwardButton from './RollForwardButton';
import {adobeAnalytics} from '@ey/voltron-analytics';
import {clickActionArgs} from '../../AdobeAnalytics/actionHelpers';
import {eventTypes, controlTypes, locationInPages, clickActionNames} from '../../AdobeAnalytics/constants';

const ProjectTile = ({project, isActiveProjects, isOtherProjects, isCompletedProjects, setIsLoading, counterState}) => {
	const [showMarkAsCompleteModal, setShowMarkAsCompleteModal] = useState(false);
	const [showRollForwardModal, setShowRollForwardModal] = useState(false);
	const [showRemoveModal, setShowRemoveModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const {projectId, name, status, owners, clients, users} = project;

	const {dashboardAccess, groupRiskAssessmentAccess, contactListAccess} = selectAccessPermissions(project);

	const userPreferences = useSelector(({userPreferences}) => userPreferences);

	const dispatch = useDispatch();
	const history = useHistory();
	const trackEvent = adobeAnalytics.useAdobeAnalyticsTrack();

	const userIsMember = userPreferences.userType.toLowerCase() === UserType.Member.toLowerCase();
	const isProjectOwner = owners ? owners?.some((owner) => owner?.userId === userPreferences.userId) : false;

	const isDraft = status === projectStatusEnum.DRAFT;
	const isActive = status === projectStatusEnum.ACTIVE;
	const isComplete = status === projectStatusEnum.COMPLETED;

	const showModules = isActiveProjects;
	const showUserLists = !isActiveProjects;
	const showRollForwardButton = isCompletedProjects;
	const showMenu = !isOtherProjects;
	const isClassicProject = !project.isGroupRiskAssessmentProject;

	const showEditMenuItem = isProjectOwner && userIsMember && !isComplete && isClassicProject;
	const showMarkAsCompleteMenuItem = isProjectOwner && userIsMember && !isComplete && !isDraft && isClassicProject;
	const showRollForwardMenuItem = userIsMember && isComplete;
	const showRemoveMenuItem = !isComplete;
	const showDeleteMenuItem = isProjectOwner && userIsMember && (isActive || isDraft);
	const showRollForwardMenuItem2 = isProjectOwner && userIsMember && isActive && isClassicProject;
	let pathname = '';

	const trackAdobeAnalyticsClickEvents = (name, location, path) => {
		// Adobe Analytics Click Tracking
		trackEvent(
			eventTypes.clickAction,
			clickActionArgs(
				name,
				controlTypes.button,
				location,
				`${window.location.origin}${path ?? ''}`
			)
		);
	};

	const handleDashboardNavigation = () => {
		pathname = `/${routes.dashboard.root}?${routes.queryString.projectId}=${projectId}`;
		history.push(pathname, null);
		trackAdobeAnalyticsClickEvents(clickActionNames.dashboard, locationInPages.projectPanel, pathname);
	};
	const handleGRANavigation = () => {
		pathname = `/${routes.gra.root}?${routes.queryString.projectId}=${projectId}`;
		history.push(pathname, null);
		trackAdobeAnalyticsClickEvents(clickActionNames.groupRiskAssessment, locationInPages.projectPanel, pathname);
	};
	const handleContactListNavigation = () => {
		pathname = `/${routes.contacts.root}?${routes.queryString.projectId}=${projectId}`;
		history.push(pathname, null);
		trackAdobeAnalyticsClickEvents(clickActionNames.contactList, locationInPages.projectPanel, pathname);
	};
	const handleWizardNavigation = (clickActionName) => {
		pathname = `/${routes.project.root}/${routes.project.classic}?${routes.queryString.projectId}=${projectId}`;
		setIsLoading(true);
		dispatch(getProjectToEdit(projectId, true)).then(() => {
			dispatch(setEditMode(true, routeToRedirectEditProject.home, null, null));
			history.push(
				pathname,
				null
			);
			setIsLoading(false);
		});

		let locationInPage;
		if(clickActionName === clickActionNames.editProject) {
			locationInPage = locationInPages.projectMenu;
		} else {
			locationInPage = locationInPages.projectPanel;
		}
		trackAdobeAnalyticsClickEvents(clickActionName, locationInPage, pathname);
	};

	const handleProjectNameClick = () => {
		if (isDraft) handleWizardNavigation(clickActionNames.projectWizard);
		else if (dashboardAccess) handleDashboardNavigation();
		else if (contactListAccess) handleContactListNavigation();
		else if (groupRiskAssessmentAccess) handleGRANavigation();
	};

	const showMarkAsCompleteModalHandler = (open) => {
		dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(open)).then(() => setShowMarkAsCompleteModal(open)));
		if(open) trackAdobeAnalyticsClickEvents(clickActionNames.markAsCompleted, locationInPages.projectMenu);
	};
	const showRollForwardModalHandler = (open) => {
		dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(open)).then(() => setShowRollForwardModal(open)));
		if(open) trackAdobeAnalyticsClickEvents(clickActionNames.rollForward, locationInPages.projectMenu);
	};
	const showRemoveModalHandler = (open) => {
		dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(open)).then(() => setShowRemoveModal(open)));
		if(open) trackAdobeAnalyticsClickEvents(clickActionNames.removeProject, locationInPages.projectMenu);
	};
	const showDeleteModalHandler = (open) => {
		dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(open)).then(() => setShowDeleteModal(open)));
		if(open) trackAdobeAnalyticsClickEvents(clickActionNames.deleteProject, locationInPages.projectMenu);
	};

	const renderName = () => {
		const props = {
			name,
			disabled: !isActiveProjects,
			onClick: handleProjectNameClick
		};

		return <Name {...props}>{isDraft && <DraftIndicator />}</Name>;
	};

	const renderMenu = () => {
		const menuItems = [
			{id: 'edit', show: showEditMenuItem, label: labels.editProject, onClick: () => handleWizardNavigation(clickActionNames.editProject)},
			{
				id: 'markAsComplete',
				show: showMarkAsCompleteMenuItem,
				label: labels.markAsCompleted,
				onClick: () => showMarkAsCompleteModalHandler(true)
			},
			{
				id: 'rollForward',
				show: showRollForwardMenuItem,
				label: labels.rollForward,
				onClick: () => showRollForwardModalHandler(true)
			},
			{
				id: 'remove',
				show: showRemoveMenuItem,
				label: labels.removeProject,
				onClick: () => showRemoveModalHandler(true)
			},
			{
				id: 'delete',
				show: showDeleteMenuItem,
				label: labels.deleteProject,
				onClick: () => showDeleteModalHandler(true)
			},
			{
				id: 'rollForward2',
				show: showRollForwardMenuItem2,
				label: labels.rollForward,
				onClick: () => showRollForwardModalHandler(true)
			}
		];

		const modalProps = {projectId, name, status};
		const markAsCompleteModalProps = {
			...modalProps,
			show: showMarkAsCompleteModal,
			setShow: showMarkAsCompleteModalHandler
		};
		const rollForwardModalProps = {
			...modalProps,
			show: showRollForwardModal,
			setShow: showRollForwardModalHandler
		};
		const removeModalProps = {
			...modalProps,
			show: showRemoveModal,
			setShow: showRemoveModalHandler
		};
		const deleteModalProps = {
			...modalProps,
			show: showDeleteModal,
			setShow: showDeleteModalHandler
		};

		const displayItems = menuItems.filter((item) => item.show);

		return (
			<>
				<MarkAsCompleteModal {...markAsCompleteModalProps} />
				<RollForwardModal {...rollForwardModalProps} />
				<RemoveModal {...removeModalProps} />
				<DeleteModal {...deleteModalProps} />
				<Menu items={displayItems} />
			</>
		);
	};

	const renderModules = () => {
		const props = {
			projectId
		};

		return (
			<StyledModules>
				{dashboardAccess && <DashboardModule {...props} onClick={handleDashboardNavigation} />}
				{groupRiskAssessmentAccess && <GRAModule {...props} onClick={handleGRANavigation} />}
				{contactListAccess && <ContactListModule {...props} onClick={handleContactListNavigation} />}
				{isDraft && <ProjectWizardModule {...props} onClick={() => handleWizardNavigation(clickActionNames.projectWizard)} />}
			</StyledModules>
		);
	};

	const renderUserLists = () => {
		const sharedProps = {
			counterState
		};

		return (
			<StyledUserLists>
				<ClientList clients={clients} {...sharedProps} />
				<ProjectOwnerList owners={owners} {...sharedProps} />
			</StyledUserLists>
		);
	};

	const renderRollForwardButton = () => {
		const props = {
			variant: 'text',
			onClick: () => showRollForwardModalHandler(true)
		};

		return <RollForwardButton {...props} />;
	};

	return (
		<CardBackground>
			<StyledNameGrid>
				{name && renderName()}
				{showMenu && renderMenu()}
			</StyledNameGrid>
			<StyledContentGrid>
				{showModules && renderModules()}
				{showUserLists && renderUserLists()}
				{showRollForwardButton && renderRollForwardButton()}
			</StyledContentGrid>
		</CardBackground>
	);
};

ProjectTile.propTypes = {
	isActiveProjects: PropTypes.bool,
	isOtherProjects: PropTypes.bool,
	isCompletedProjects: PropTypes.bool,
	setIsLoading: PropTypes.func
};

ProjectTile.defaultProps = {
	isActiveProjects: false,
	isOtherProjects: false,
	isCompletedProjects: false,
	setIsLoading: () => null
};

export default ProjectTile;
